import React, { useState, useCallback, useEffect } from 'react';
import { ArrowLeft, Plus, Edit2, Trash2, Search, Users, Mail } from 'lucide-react';
import { Personal, Area } from '../../types';
import ApiService from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';
import ErrorMessage from '../ErrorMessage';

interface PersonalConfigProps {
  onBack: () => void;
}

interface PersonalFormData {
  nombre: string;
  apellido: string;
  email: string;
  rol: string;
  areaId: number | null;
  disponible: boolean;
  fechaIngreso: string;
}

const PersonalConfig: React.FC<PersonalConfigProps> = ({ onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPersonal, setEditingPersonal] = useState<Personal | null>(null);
  
  const [formData, setFormData] = useState<PersonalFormData>({
    nombre: '',
    apellido: '',
    email: '',
    rol: '',
    areaId: null,
    disponible: true,
    fechaIngreso: new Date().toISOString().split('T')[0]
  });

  // Estados para datos
  const [personal, setPersonal] = useState<Personal[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  // Fetch manual para evitar loops de useApi
  const fetchPersonal = useCallback(async () => {
    try {
      console.log('📊 Cargando personal...');
      const personalData = await ApiService.getPersonal();
      setPersonal(personalData);
      console.log('✅ Personal cargado:', personalData.length);
    } catch (err: any) {
      console.error('❌ Error cargando personal:', err);
      setError(err.message || 'Error cargando personal');
    }
  }, []);

  const fetchAreas = useCallback(async () => {
    try {
      console.log('🏢 Cargando áreas...');
      const areasData = await ApiService.getAreas();
      setAreas(areasData);
      console.log('✅ Áreas cargadas:', areasData.length);
    } catch (err: any) {
      console.error('❌ Error cargando áreas:', err);
    }
  }, []);

  // Función para recargar datos
  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchPersonal(), fetchAreas()]);
    } finally {
      setLoading(false);
    }
  }, [fetchPersonal, fetchAreas]);

  // Cargar datos inicial
  useEffect(() => {
    refetch();
  }, [refetch]);

  // CARGA DE FOTOS DESACTIVADA COMPLETAMENTE PARA EVITAR BUCLES MSAL

  // Función para obtener iniciales
  const getInitials = (nombre: string, apellido?: string) => {
    return `${nombre.charAt(0)}${apellido ? apellido.charAt(0) : ''}`.toUpperCase();
  };

  // Filtrar personal por búsqueda
  const filteredPersonal = personal?.filter(person =>
    person.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (person.apellido && person.apellido.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (person.email && person.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    person.rol.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (person.area && person.area.nombre.toLowerCase().includes(searchTerm.toLowerCase()))
  ) || [];

  // Manejar apertura de modal
  const handleOpenModal = (person?: Personal) => {
    if (person) {
      setEditingPersonal(person);
      setFormData({
        nombre: person.nombre,
        apellido: person.apellido || '',
        email: person.email || '',
        rol: person.rol,
        areaId: person.area?.id || null,
        disponible: person.disponible,
        fechaIngreso: person.fechaIngreso ? new Date(person.fechaIngreso).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
      });
    } else {
      setEditingPersonal(null);
      setFormData({
        nombre: '',
        apellido: '',
        email: '',
        rol: '',
        areaId: areas?.[0]?.id || null,
        disponible: true,
        fechaIngreso: new Date().toISOString().split('T')[0]
      });
    }
    setIsModalOpen(true);
  };

  // Cerrar modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPersonal(null);
    setFormData({
      nombre: '',
      apellido: '',
      email: '',
      rol: '',
      areaId: null,
      disponible: true,
      fechaIngreso: new Date().toISOString().split('T')[0]
    });
  };

  // Manejar envío del formulario
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nombre.trim() || !formData.rol.trim()) {
      alert('El nombre y el rol son obligatorios');
      return;
    }

    const submitData = {
      ...formData,
      areaId: formData.areaId || undefined,
      fechaIngreso: formData.fechaIngreso ? new Date(formData.fechaIngreso).toISOString() : undefined
    };

    try {
      if (editingPersonal) {
        setUpdating(true);
        console.log('🔄 Actualizando personal...');
        await ApiService.updatePersonal(editingPersonal.id, submitData);
        console.log('✅ Personal actualizado');
      } else {
        setCreating(true);
        console.log('🔄 Creando personal...');
        await ApiService.createPersonal(submitData);
        console.log('✅ Personal creado');
      }
      
      handleCloseModal();
      await refetch();
    } catch (error) {
      console.error('❌ Error al guardar personal:', error);
      alert('Error al guardar personal');
    } finally {
      setCreating(false);
      setUpdating(false);
    }
  };

  // Eliminar personal
  const handleDelete = async (id: number, nombre: string, apellido?: string) => {
    const fullName = `${nombre}${apellido ? ` ${apellido}` : ''}`;
    if (!window.confirm(`¿Estás seguro de que deseas eliminar a "${fullName}"?`)) {
      return;
    }

    try {
      console.log('🔄 Eliminando personal...');
      await ApiService.deletePersonal(id);
      console.log('✅ Personal eliminado');
      await refetch();
    } catch (error) {
      console.error('❌ Error al eliminar personal:', error);
      alert('Error al eliminar personal');
    }
  };


  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="flex items-center space-x-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Volver</span>
          </button>
          <div className="flex items-center space-x-2">
            <Users className="w-6 h-6 text-primary dark:text-primary-dark" />
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">Gestión de Personal</h1>
          </div>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          <span>Nueva Persona</span>
        </button>
      </div>

      {/* Indicador de progreso DESACTIVADO */}

      {/* Búsqueda */}
      <div className="card p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 dark:text-neutral-500 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar personal..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-10"
          />
        </div>
      </div>

      {/* Lista de personal */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
            <thead className="bg-neutral-50 dark:bg-neutral-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Área
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-neutral-900 divide-y divide-neutral-200 dark:divide-neutral-700">
              {filteredPersonal.map((person) => (
                <tr key={person.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-all">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary/10 dark:bg-primary-dark/10 rounded-full flex items-center justify-center">
                        <span className="text-primary dark:text-primary-dark font-semibold text-sm">
                          {getInitials(person.nombre, person.apellido)}
                        </span>
                      </div>
                      <div className="ml-4">
                        <div className="font-medium text-neutral-900 dark:text-neutral-50">
                          {person.nombre} {person.apellido}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-400">
                      <Mail className="w-4 h-4 mr-2" />
                      {person.email || 'Sin email'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="badge text-xs">
                      {person.rol}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-neutral-900 dark:text-neutral-50">
                      {person.area?.nombre || 'Sin área asignada'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`badge text-xs ${
                      person.disponible
                        ? 'bg-success/10 dark:bg-success-dark/10 text-success dark:text-success-dark'
                        : 'bg-error/10 dark:bg-error/10 text-error dark:text-error'
                    }`}>
                      {person.disponible ? 'Disponible' : 'No disponible'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleOpenModal(person)}
                        className="text-primary dark:text-primary-dark hover:text-primary-hover p-1 rounded"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(person.id, person.nombre, person.apellido)}
                        className="text-error hover:text-error-hover p-1 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredPersonal.length === 0 && (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
              <h3 className="mt-2 text-sm font-medium text-neutral-900 dark:text-neutral-50">No hay personal</h3>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                {searchTerm ? 'No se encontraron personas con ese criterio' : 'Comienza agregando una nueva persona al equipo'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar personal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-900 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50 mb-4">
              {editingPersonal ? 'Editar Personal' : 'Nueva Persona'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    className="input-field"
                    placeholder="Nombre de la persona"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Apellido
                  </label>
                  <input
                    type="text"
                    value={formData.apellido}
                    onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                    className="input-field"
                    placeholder="Apellido"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="input-field"
                  placeholder="email@ejemplo.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Rol *
                  </label>
                  <select
                    value={formData.rol}
                    onChange={(e) => setFormData({ ...formData, rol: e.target.value })}
                    className="input-field"
                    required
                  >
                    <option value="">Selecciona un rol</option>
                    <option value="Head of">Head of</option>
                    <option value="Team Leader">Team Leader</option>
                    <option value="Consultor">Consultor</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Área
                  </label>
                  <select
                    value={formData.areaId || ''}
                    onChange={(e) => setFormData({ ...formData, areaId: e.target.value ? parseInt(e.target.value) : null })}
                    className="input-field"
                  >
                    <option value="">Sin área asignada</option>
                    {areas?.map((area) => (
                      <option key={area.id} value={area.id}>
                        {area.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Fecha de Ingreso
                  </label>
                  <input
                    type="date"
                    value={formData.fechaIngreso}
                    onChange={(e) => setFormData({ ...formData, fechaIngreso: e.target.value })}
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Estado
                  </label>
                  <div className="flex items-center space-x-4 mt-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="disponible"
                        checked={formData.disponible}
                        onChange={() => setFormData({ ...formData, disponible: true })}
                        className="mr-2"
                      />
                      Disponible
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="disponible"
                        checked={!formData.disponible}
                        onChange={() => setFormData({ ...formData, disponible: false })}
                        className="mr-2"
                      />
                      No disponible
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-neutral-700 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-800 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={creating || updating}
                  className="px-4 py-2 btn-primary transition-colors disabled:opacity-50"
                >
                  {creating || updating ? 'Guardando...' : editingPersonal ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalConfig;